import { library } from "@fortawesome/fontawesome-svg-core";
import { faArrowDown, faArrowUp, faBaby } from "@fortawesome/free-solid-svg-icons"; // up and down arrows that are placed before export- and import values
import Chart from "chart.js";
import moment from "moment";
import { hapticFeedback } from "./app/haptic-feedback";
import { colors } from "./colors";
import { views } from "./helpers/views";
import "./styles/tooltips.css";

/**
 * @typedef TooltipHomeTimestamp
 * @property {import("moment").Moment} date The date object,
 * @property {boolean} isCurrentYear
 *
 * @typedef {import("chart.js").ChartTooltipItem & { timestamp: TooltipHomeTimestamp }} TooltipItem
 */

library.add(faArrowUp, faArrowDown, faBaby)

const getMomentFormat = (locale, currentYear, view) => {
    var otherFormats = {
        hour: `dd D MMM ${currentYear ? '' : 'YYYY'} HH:mm`,
        day: `dd D MMM ${currentYear ? '' : 'YYYY'} HH:mm`,
        week: `dd D MMM ${currentYear ? '' : 'YYYY'}`,
        month: `MMMM ${currentYear ? '' : 'YYYY'} - [week] w`,
        year: "MMMM YYYY"
    };

    let otherFormat = otherFormats[view]

    const localeDefaults = {
        lastWeek: otherFormat,
        nextWeek: otherFormat,
        sameElse: otherFormat
    }

    let format = {
        'da': {
            calendar: {
                ...localeDefaults,
                lastDay: `[I går] ${view === 'week' ? '' : 'HH:mm'}`,
                sameDay: `[I dag] ${view === 'week' ? '' : 'HH:mm'}`,
            }
        },
        'de': {
            calendar: {
                ...localeDefaults,
                lastDay: `[Gestern] ${view === 'week' ? '' : 'HH:mm'}`,
                sameDay: `[Heute] ${view === 'week' ? '' : 'HH:mm'}`,
            }
        },
        'en': {
            calendar: {
                ...localeDefaults,
                lastDay: `[Yesterday] ${view === 'week' ? '' : 'HH:mm'}`,
                sameDay: `[Today] ${view === 'week' ? '' : 'HH:mm'}`,
            }
        },
        'fi': {
            calendar: {
                ...localeDefaults,
                lastDay: `[Eilen] ${view === 'week' ? '' : 'HH:mm'}`,
                sameDay: `[Tänään] ${view === 'week' ? '' : 'HH:mm'}`,
            }
        },
        'fr': {
            calendar: {
                ...localeDefaults,
                lastDay: `[Hier] ${view === 'week' ? '' : 'HH:mm'}`,
                sameDay: `[Aujourd'hui] ${view === 'week' ? '' : 'HH:mm'}`,
            }
        },
        'hu': {
            calendar: {
                ...localeDefaults,
                lastDay: `[Tegnap] ${view === 'week' ? '' : 'HH:mm'}`,
                sameDay: `[Ma] ${view === 'week' ? '' : 'HH:mm'}`,
            }
        },
        'nb': {
            calendar: {
                ...localeDefaults,
                lastDay: `[I går] ${view === 'week' ? '' : 'HH:mm'}`,
                sameDay: `[I dag] ${view === 'week' ? '' : 'HH:mm'}`,
            }
        },
        'nl': {
            calendar: {
                ...localeDefaults,
                lastDay: `[Gisteren] ${view === 'week' ? '' : 'HH:mm'}`,
                sameDay: `[Vandaag] ${view === 'week' ? '' : 'HH:mm'}`,
            }
        },
        'sv': {
            calendar: {
                ...localeDefaults,
                lastDay: `[Igår] ${view === 'week' ? '' : 'HH:mm'}`,
                sameDay: `[Idag] ${view === 'week' ? '' : 'HH:mm'}`,
            }
        },
    }

    let returnFormat = format[locale]

    if (views[view] >= views.month) {
        returnFormat.calendar.lastDay = otherFormat
        returnFormat.calendar.sameDay = otherFormat
    }

    return returnFormat
}

/**
 * @type {import("chart.js").ChartTooltipOptions}
 */
const tooltipConfiguration = {
    enabled: false,
    bodyFontFamily: `'Lato Semibold', ${Chart.defaults.global.defaultFontFamily}`,
    mode: 'index',
    intersect: false,
    bodyFontSize: 16,

    /**
     * Extracts the timestamp from the xLabel (which is a string containing the
     * timestamp in milliseconds in the home's timezone) and filters out invalid
     * timestamps, or dates in the future.
     *
     * Adds a `timestamp` property to the tooltip item, which contains the date
     * (in the home's timezone), and a boolean indicating whether that date is
     * in the current year (which is used to determine whether to show the year
     * in the tooltip).
     *
     * @param {TooltipItem} tooltipItem
     * @param {import("./chart-configuration").GraphData} data
     */
    filter: (tooltipItem, data) => {
        const { dateSupport } = data.hw

        const timestampMillis = Number(tooltipItem.xLabel)
        if (Number.isNaN(timestampMillis)) {
            return false
        }

        const date = dateSupport.getDateFromMillis(timestampMillis)
        if (dateSupport.isInFuture(date)) {
            return false
        }

        tooltipItem.timestamp = {
            date: date,
            isCurrentYear: dateSupport.isInCurrentYear(date)
        }

        return true
    },
    callbacks: {
        label: (tooltipItem, data) => ({tooltipItem, data})
    }
}

/**
 * @param {import("chart.js").ChartTooltipModel} tooltipModel
 */
const customTooltips = (tooltipModel) => {
    let tooltipEl = document.getElementById('chartjs-tooltip')
    let chartEl = document.getElementById('chart')
    let chartWidth = chartEl.clientWidth;
    let chartHeight = chartEl.clientHeight;

    if (!tooltipEl) {
        tooltipEl = document.createElement('div');
        tooltipEl.id = 'chartjs-tooltip';
        chartEl.parentNode.appendChild(tooltipEl);
    }

    if (isNaN(parseFloat(tooltipModel?.body?.[0]?.lines?.[0]?.tooltipItem.yLabel))) {
        return
    }

    /**
     * @type {{tooltipItem: TooltipItem, data: import("./chart-configuration").GraphData}}
     */
    const label = tooltipModel.body[0].lines[0]
    const tooltipItem = label.tooltipItem

    const { type, lang, view, three_phases, showBatteryPercentage } = label.data.hw
    const { index: ttIndex, timestamp } = tooltipItem

    const isSolarGraph = type === 'solar' || type === 'solar_combined'

    /**
     * @type {number} number of digits in fractional part of measurement (precision)
     * gas measurement: always 3
     * power:           2 for total/bar views (week, month, etc.), 0 for wattage/line views
     */
    let fractionDigits = type === 'gas' ? 3 : views[view] >= views.week ? 2 : 0

    // unit
    let unit
    if (type === 'water') {
        fractionDigits = 0
        unit = 'L' // TODO unit should be L/5m eventually
    } else if (type === 'temperature' || type === 'overvoltage') {
        fractionDigits = 1
    } else if (type === 'humidity') {
        fractionDigits = 0
    } else {
        unit = type === 'gas' ? 'm<sup>3</sup>' : (views[view] <= 1 ? 'W' : 'kWh')
    }

    // icon
    const getIcon = (value) => {
        const icon = value >= 0 && !isSolarGraph ? 'fa-arrow-down' : 'fa-arrow-up'

        return  `<i class="fas ${icon} fa-sm"></i>`
    }

    // Format value with number of fraction digits, and in the correct locale (3.23 vs 3,23, etc.)
    const formatValue = (value, makeAbsolute = true) => {
        const newValue = makeAbsolute ? Math.abs(value) : value
        return newValue.toLocaleString(lang, {
            minimumFractionDigits:fractionDigits,
            maximumFractionDigits:fractionDigits
        })
    }

    let yLabel = ''

    if (three_phases && views[view] <= views.day) {
        // Create "L1: ↓20W L2: ↓0W L3: ↑121W" label
        for (let i of [1,2,3]) {
            let value = label?.data?.datasets[i-1]?.data[ttIndex]?.y || 0

            let color = value >= 0 && !isSolarGraph
                ? colors[`power_import_l${i}`].border
                : colors[`power_export_l${i}`].border

            yLabel += `<span style="color: ${color}">L${i}: ${getIcon(value)} ${formatValue(value)} ${unit} </span>`
        }
    } else if (type === 'self_consumption') {
        let labelValue

        const imported = label.data.datasets[0].data[ttIndex].y
        const exported = label.data.datasets[1].data[ttIndex].y
        const selfConsumed = label.data.datasets[2].data[ttIndex].y

        if (views[view] <= views.day) {
            yLabel += `<span style="color: ${colors.hover.self_consumption}"><img src="/sc_arrow.svg"/> ${formatValue(selfConsumed)} ${unit} </span>`
            yLabel += `<span style="color: ${colors.hover.import}"><i class="fas fa-arrow-down fa-sm"></i> ${formatValue(imported)} ${unit} </span>`
            yLabel += `<span style="color: ${colors.hover.export}"><i class="fas fa-arrow-up fa-sm"></i> ${formatValue(exported)} ${unit} </span>`

        } else {
            const importedHtml = `<span style="color: ${colors.hover.import}"><i class="fas fa-arrow-down fa-sm"></i> ${formatValue(imported)} ${unit}</span>`
            const exportedHtml = `<span style="color: ${colors.hover.export}"><i class="fas fa-arrow-up fa-sm"></i> ${formatValue(exported)} ${unit}</span>`
            const selfConsumedHtml = `<span style="color: ${colors.hover.self_consumption}"><img src="/sc_arrow.svg"/> ${formatValue(selfConsumed)} ${unit}</span>`
            yLabel = `${selfConsumedHtml} ${importedHtml} ${exportedHtml}`
        }
    } else if (type === 'temperature') {
        unit = '°'

        if (views[view] == views.day) {
            const meanValue = label.data.datasets[0].data[ttIndex].y

            yLabel += `<span style="color: ${colors.hover.temperature_mean}"> ${formatValue(meanValue, false)}${unit}</span>`
        } else {
            const meanValue = label.data.datasets[0].data[ttIndex].y
            const minValue = label.data.datasets[1].data[ttIndex].y
            const maxValue = label.data.datasets[2].data[ttIndex].y

            yLabel += `<span style="color: ${colors.hover.temperature_max}">${formatValue(maxValue, false)}${unit}</span>`
            yLabel += `<span style="color: ${colors.hover.temperature_mean}"> ${formatValue(meanValue, false)}${unit}</span>`
            yLabel += `<span style="color: ${colors.hover.temperature_min}"> ${formatValue(minValue, false)}${unit}</span>`
        }
    } else if (type === 'humidity') {
        unit = '%'

        if (views[view] == views.day) {
            const meanValue = label.data.datasets[0].data[ttIndex].y

            yLabel += `<span style="color: ${colors.hover.humidity_mean}"> ${formatValue(meanValue)}${unit}</span>`
        } else {
            const meanValue = label.data.datasets[0].data[ttIndex].y
            const minValue = label.data.datasets[1].data[ttIndex].y
            const maxValue = label.data.datasets[2].data[ttIndex].y

            yLabel += `<span style="color: ${colors.hover.humidity_max}">${formatValue(maxValue)}${unit}</span>`
            yLabel += `<span style="color: ${colors.hover.humidity_mean}"> ${formatValue(meanValue)}${unit}</span>`
            yLabel += `<span style="color: ${colors.hover.humidity_min}"> ${formatValue(minValue)}${unit}</span>`
        }
    } else if (type === 'overvoltage') {
        unit = 'V'

        if (label.data.datasets[1].data[ttIndex] !== undefined &&
            label.data.datasets[1].data[ttIndex].y !== null) {
            const l1 = label.data.datasets[1].data[ttIndex].y
            yLabel += `<span style="color: ${colors.hover.voltage_l1}">${formatValue(l1)}${unit}</span>`
        }

        if (label.data.datasets[2].data[ttIndex] !== undefined &&
            label.data.datasets[2].data[ttIndex].y !== null) {
            const l2 = label.data.datasets[2].data[ttIndex].y
            yLabel += `<span style="color: ${colors.hover.voltage_l2}"> ${formatValue(l2)}${unit}</span>`
        }

        if (label.data.datasets[3].data[ttIndex] !== undefined &&
            label.data.datasets[3].data[ttIndex].y !== null) {
            const l3 = label.data.datasets[3].data[ttIndex].y
            yLabel += `<span style="color: ${colors.hover.voltage_l3}"> ${formatValue(l3)}${unit}</span>`
        }
    } else if (type === 'battery') {
        if (label.data.datasets[0].data[ttIndex] !== undefined &&
            label.data.datasets[0].data[ttIndex].y !== null) {
            const imported = label.data.datasets[0].data[ttIndex].y
            const color = imported >= 0 ? colors.hover.import : colors.hover.export
            yLabel += `<span style="color: ${color}">${formatValue(imported)}${unit}</span>`
        }

        // Based on view show either battery percentage or export value
        if (views[view] == views.day) {
            if (showBatteryPercentage &&
                label.data.datasets[1].data[ttIndex] !== undefined &&
                label.data.datasets[1].data[ttIndex].y !== null) {
                const percentage = label.data.datasets[1].data[ttIndex].y
                yLabel += `<span style="color: ${colors.datalabels}"> ${formatValue(percentage)}%</span>`
            }
        } else {
            if (label.data.datasets[1].data[ttIndex] !== undefined &&
                label.data.datasets[1].data[ttIndex].y !== null) {
                const exported = label.data.datasets[1].data[ttIndex].y
                yLabel += `<span style="color: ${colors.hover.export}"> ${formatValue(exported)}${unit}</span>`
            }
        }

    } else {
        let nettoValue

        if (type === 'standby_usage') {
            nettoValue = label.data.datasets[1].data[ttIndex].y || 0
        } else {
            // Calculate netto when number of datasets > 1; round result to 0, 2 or 3 decimals // @todo move netto calculation to API !?
            nettoValue = label.data.datasets
                .map(dataset => dataset.data[ttIndex] && dataset.data[ttIndex].y || 0)
                .reduce((p, n) => Math.round((p - n) * Math.pow(10, fractionDigits)) / Math.pow(10, fractionDigits))
        }
        let color

        if (type === 'water') {
            color = colors.hover.water
        } else {
            color = type === 'gas'
                ? colors.hover.gas
                : nettoValue >= 0 && !isSolarGraph ? colors.hover.import : colors.hover.export;
        }

        yLabel = `<span style="color: ${color}">${getIcon(nettoValue)} ${formatValue(nettoValue)} ${unit}</span>`
    }

    if (yLabel === "") {
        return
    }

    hapticFeedback('light')

    moment.locale(lang, {
        ...getMomentFormat(
            lang,
            timestamp.isCurrentYear,
            view
        ),

        // ISO-8601, Europe
        week: {
            dow: 1, // First day of week is Monday
            doy: 4  // First week of year must contain 4 January (7 + 1 - 4)
        }
    })

    moment.locale('')

    let valueWrExtraClass = ''

    if (label.tooltipItem.x / chartWidth <= 0.30) {
        valueWrExtraClass = 'value_wr_left'
    }

    if (label.tooltipItem.x / chartWidth >= 0.70) {
        valueWrExtraClass = 'value_wr_right'
    }

    let vlHeight = `${chartHeight - 70}px`

    let innerHtml = `
            <div class="value_wr ${valueWrExtraClass}">
                <div class="time" style="color: ${colors.hover.time}">
                    ${timestamp.date.locale(lang).calendar()}
                </div>
                <div class="value">
                    ${yLabel}
                </div>
            </div>
            <div class="vl" style="height: ${vlHeight};"></div>
        `

    tooltipEl.innerHTML = innerHtml

    // Positioning
    tooltipEl.style.left = tooltipModel.caretX + 'px';

    // Styles
    tooltipEl.style.fontFamily = tooltipModel._bodyFontFamily;
    tooltipEl.style.fontSize = tooltipModel.bodyFontSize + 'px';
    tooltipEl.style.fontStyle = tooltipModel._bodyFontStyle;
}

export { customTooltips, tooltipConfiguration };

